import type { SelectChangeEvent } from '@mui/material'
import { Alert, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import IcBaselineBookmarkAdded from '~icons/ic/baseline-bookmark-added'
import IcBaselineCheck from '~icons/ic/baseline-check'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineKeyboardArrowDown from '~icons/ic/baseline-keyboard-arrow-down'
import IcBaselineKeyboardArrowUp from '~icons/ic/baseline-keyboard-arrow-up'
import IcBaselinePushPin from '~icons/ic/baseline-push-pin'
import IcOutlineBookmarkAdd from '~icons/ic/outline-bookmark-add'
import IcOutlineReportProblem from '~icons/ic/outline-report-problem'
import { openSignModal } from 'src/components/Account/utils'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { Preloader } from 'src/components/Preloader/Preloader'
import { UIButton } from 'src/components/ui/Button'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import type {
  GenerationModelBaseFragment,
  GenerationModelDetailFragment,
  GenerationModelVersionBaseFragment,
  GenerationModelVersionPreviewFragment,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelMarkType } from 'src/services/apollo/generated/graphql'
import {
  executeMarkGenerationModel,
  useGetGenerationModel,
  useListGenerationModelVersions,
} from 'src/services/apollo/generation-model'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { getNodesFromConnection } from 'src/utils/graphql'
import { mdUser } from 'src/utils/markdown'
import { getThumbnailUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { ModelFormContext } from './ModelSelectContext'

function ModelDescription({ description }: { description: string }) {
  const { t } = useTranslation(['market'])

  const ref = useRef<HTMLDivElement>(null)
  const [expand, setExpand] = useState<boolean>(false)
  const [isLongDesc, setIsLongDesc] = useState(false)

  useEffect(() => {
    if (!ref.current) return
    setIsLongDesc(ref.current.scrollHeight > ref.current.clientHeight)
  }, [])

  return (
    <section>
      <h4 className="pb-1 font-medium">
        {t('market:model.description.title')}
      </h4>

      <div>
        <p
          ref={ref}
          className={cx(
            expand
              ? // break-words with unsepcified width needs display: table,
                // which is incompatible with line-clamp
                'table table-fixed break-words w-full'
              : 'line-clamp-4 break-all',
            'text-sm whitespace-pre-line',
          )}
        >
          <MarkdownRenderer renderer={mdUser}>{description}</MarkdownRenderer>
        </p>

        {isLongDesc && (
          <button
            className="flex items-center"
            onClick={() => setExpand(v => !v)}
          >
            {expand ? (
              <IcBaselineKeyboardArrowUp />
            ) : (
              <IcBaselineKeyboardArrowDown />
            )}

            <div className="text-sm opacity-75">
              {expand ? t('artwork:show.less') : t('artwork:show.more')}
            </div>
          </button>
        )}
      </div>
    </section>
  )
}

function ModelParamsInfo({
  version,
}: {
  version?: Pick<GenerationModelVersionBaseFragment, 'extra'>
}) {
  const { t } = useTranslation(['market', 'generation'])

  if (!version?.extra) return null

  const params = [
    [t('generation:sampling-steps.label'), version.extra.samplingSteps],
    [t('generation:sampling-method.label'), version.extra.samplingMethod],
    [t('generation:cfg-scale.label'), version.extra.cfgScale],
    [t('generation:negative-prompts.label'), version.extra.negativePrompts],
  ].filter(([, value]) => value != null)

  if (params.length === 0) return null

  return (
    <section>
      <h4 className="pb-1 font-medium">{t('market:model.parameters.title')}</h4>

      <ul className="flex flex-col gap-2 text-sm">
        {params.map(([title, value], index) => (
          <li key={index}>
            <h5>{title}</h5>
            <span className="text-primary-light">{value}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

// TODO: Reuse components from ModelInfoSection.tsx
function ModelPermsInfo({
  model,
}: {
  model: Pick<GenerationModelBaseFragment, 'extra' | 'isDownloadable'>
}) {
  const { t } = useTranslation(['market'])

  const modelPerms = model.extra?.permissions ?? {}

  const perms = [
    [t('market:model.permissions.allow-generate-use'), true],
    [t('market:submit.allow-download'), model.isDownloadable ?? false],
    [
      t('market:model.permissions.commercial-uses'),
      modelPerms.commercialUses ?? modelPerms['commercial-uses'] ?? false,
    ],
  ]

  return (
    <section>
      <h4 className="pb-1 font-medium">
        {t('market:model.permissions.title')}
      </h4>

      <ul className="flex flex-col gap-.5">
        {perms.map(([title, value], index) => {
          return (
            <li key={index} className="flex gap-1 items-center text-sm">
              {value ? (
                <IcBaselineCheck className="text-green-600 size-6 shrink-0" />
              ) : (
                <IcBaselineClose className="text-rose-600 size-6 shrink-0" />
              )}

              <span>{title}</span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function ModelBookmarkButton({
  model,
}: {
  model: Pick<GenerationModelDetailFragment, 'id' | 'bookmarked'>
}) {
  const { me } = useMe()
  const hasLoggedIn = !!me?.id
  const { t } = useTranslation(['market', 'generation'])

  const isBookmarked = model.bookmarked

  const [loading, handleBookmark] = useLoadingCallback(async () => {
    if (!hasLoggedIn) return openSignModal()
    try {
      const target = !isBookmarked
      track(target ? 'bookmark_click' : 'delete_bookmark_click', {
        scene: 'model-select-preview',
        modelId: model.id,
      })

      await executeMarkGenerationModel({
        modelId: model.id,
        target,
        type: GenerationModelMarkType.Bookmark,
      })

      toast.info(
        target
          ? t('market:model.notice.bookmark-success')
          : t('market:model.notice.unbookmark-success'),
      )
    } catch (err: any) {
      if (err === UserCancelled) return
      toast.error(`Failed to bookmark. ${err?.message}`)
    }
  }, [isBookmarked, model.id, hasLoggedIn])

  return (
    <UIButton
      variant="filled"
      size="small"
      className="rounded-full"
      color="normal"
      colorScheme="dark"
      busy={loading}
      endIcon={
        isBookmarked ? (
          <IcBaselineBookmarkAdded className="text-theme-primary size-4" />
        ) : (
          <IcOutlineBookmarkAdd className="size-4" />
        )
      }
      onPress={handleBookmark}
    >
      {isBookmarked
        ? t('generation:model.preview.bookmarked')
        : t('market:model.action.bookmark')}
    </UIButton>
  )
}

function ModelPinButton({
  model,
  mutate,
}: {
  model: Pick<GenerationModelDetailFragment, 'id' | 'bookmarked'>
  mutate?: () => void
}) {
  const { t } = useTranslation(['market'])

  const isBookmarked = model.bookmarked

  const [loading, handleBookmark] = useLoadingCallback(async () => {
    track('model-select-preview-pin-to-top', {
      modelId: model.id,
    })
    try {
      await executeMarkGenerationModel({
        modelId: model.id,
        target: false,
        type: GenerationModelMarkType.Bookmark,
      })
      await executeMarkGenerationModel({
        modelId: model.id,
        target: true,
        type: GenerationModelMarkType.Bookmark,
      })

      toast.info(t('generation:model.preview.notice.pin-to-top-success'))

      mutate?.()
    } catch (err: any) {
      if (err === UserCancelled) return
      toast.error(`Failed to pin. ${err?.message}`)
    }
  }, [model.id])

  if (!isBookmarked) return null

  return (
    <UIButton
      variant="filled"
      size="small"
      color="normal"
      colorScheme="dark"
      className="rounded-full"
      busy={loading}
      endIcon={<IcBaselinePushPin className="text-theme-primary size-4" />}
      onPress={handleBookmark}
    >
      {t('generation:model.preview.action.pin-to-top')}
    </UIButton>
  )
}

function ModelVersionMedia({
  version,
}: {
  version?: Pick<GenerationModelVersionBaseFragment, 'media'>
}) {
  return version?.media ? (
    <img
      loading="lazy"
      decoding="async"
      className="inline-block align-bottom mr-2 w-[1.4em] h-[1.4em] rounded object-cover"
      src={getThumbnailUrl(version.media)}
    />
  ) : null
}

export function ModelVersionSelect({
  versionId,
  versions,
  onChange,
}: {
  versionId: string
  versions: Pick<
    GenerationModelVersionBaseFragment,
    'id' | 'name' | 'media' | 'updatedAt'
  >[]
  onChange: (event: SelectChangeEvent) => void
}) {
  const { t } = useTranslation(['market'])

  return (
    <FormControl className="mt-2" fullWidth size="small">
      <InputLabel id="model-select-version-select-label">
        {t('market:submit.version-form.version-string')}
      </InputLabel>
      <Select
        MenuProps={{
          // Temporary fix for usability
          // TODO: migrate to RAC
          disablePortal: true,
        }}
        labelId="model-select-version-select-label"
        label={t('market:submit.version-form.version-string')}
        value={versionId}
        renderValue={value => {
          const ver = versions.find(v => v.id === value)
          return (
            <>
              <ModelVersionMedia version={ver} />
              {ver?.name}
            </>
          )
        }}
        onChange={onChange}
      >
        {versions.map(ver => (
          <MenuItem key={ver.id} value={ver.id}>
            <div>
              <ModelVersionMedia version={ver} />
              {ver.name}

              <div className="mt-1 text-xs">
                {dayjs(ver.updatedAt).format('lll')}
              </div>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function ModelSelectPreviewInner({
  modelId,
  onClose,
  onConfirm,
  showPinToTop,
  mutateAfterPin,
  ...props
}: {
  modelId: string
  onClose: () => void
  onConfirm: VersionConfirmHandler
  showPinToTop?: boolean
  mutateAfterPin?: () => void
} & StyleProps) {
  const { t } = useTranslation(['market', 'generation'])

  const [modelFormState, setModelFormState] = useContext(ModelFormContext)

  const { data: model, loading: loadingModel } = useGetGenerationModel(modelId)
  const modelImage = getThumbnailUrl(model?.media)

  const { data: versionsData, loading: loadingVersions } =
    useListGenerationModelVersions({
      modelId,
      first: 60,
    })
  const versions = getNodesFromConnection(versionsData)

  const versionId = modelFormState.versionId
  const handleVersionIdChange = useCallback(
    (event: SelectChangeEvent) => {
      setModelFormState(state => ({
        ...state,
        versionId: event.target.value,
      }))
    },
    [setModelFormState],
  )

  const selectedVer = useMemo(
    () => versions?.find(v => v.id === versionId),
    [versions, versionId],
  )

  const onConfirmClick = () => {
    if (!modelFormState.type || !versionId || loadingModel || loadingVersions)
      return onClose()

    track('model_selected', { location: modelFormState.type })
    onConfirm({
      id: versionId,
      version: selectedVer,
      // NOTE: Have to explicitly pass back a valid model entity
      // Otherwise can't properly import all model default settings
      model,
    })

    return onClose()
  }

  if (!model || !versionsData)
    return (
      <div {...props}>
        <Preloader />
      </div>
    )

  return (
    <div {...props} className={cx('flex flex-col', props.className)}>
      <div className="flex-1 flex flex-col p-4 gap-3 overflow-auto">
        <div className="relative">
          <img
            loading="lazy"
            decoding="async"
            className="w-full aspect-square rounded-[0.96rem] object-cover"
            src={modelImage}
          />

          <div className="absolute top-1 right-1 flex flex-col gap-1 items-end">
            <ModelBookmarkButton model={model} />
            {showPinToTop && (
              <ModelPinButton model={model} mutate={mutateAfterPin} />
            )}
          </div>
        </div>

        <Link to={`/model/${model.id}`} target="_blank">
          <h3 className="text-xl break-all font-semibold">{model?.title}</h3>
        </Link>

        {model.extra?.description?.trim() && (
          <ModelDescription
            // keyed to make sure height is recalculated
            key={model.extra.description}
            description={model.extra.description}
          />
        )}

        <ModelParamsInfo version={selectedVer} />

        <ModelPermsInfo model={model} />
      </div>

      <div className="flex flex-col gap-3 p-4">
        {versions.length && versionId ? (
          versions.length > 1 ? (
            <ModelVersionSelect
              versionId={versionId}
              versions={versions}
              onChange={handleVersionIdChange}
            />
          ) : null
        ) : (
          <Alert
            severity="warning"
            className="px-3 py-0"
            icon={<IcOutlineReportProblem />}
          >
            {t('generation:model.preview.no-version')}
          </Alert>
        )}

        <UIButton
          busy={loadingModel || loadingVersions}
          disabled={!selectedVer}
          variant="filled"
          onPress={onConfirmClick}
        >
          {t('market:model.use-this-model')}
        </UIButton>
      </div>
    </div>
  )
}

export default function ModelSelectPreview(
  props: {
    onClose: () => void
    onConfirm: VersionConfirmHandler
    showPinToTop?: boolean
    mutateAfterPin?: () => void
  } & StyleProps,
) {
  const { t } = useTranslation(['generation'])
  const [modelFormState] = useContext(ModelFormContext)
  const modelId = modelFormState.modelId

  if (!modelId)
    return (
      <div
        className={cx(
          'grid place-items-center text-neutral-400',
          props.className,
        )}
        style={props.style}
      >
        {t('generation:model.preview.no-selection')}
      </div>
    )

  return <ModelSelectPreviewInner {...props} modelId={modelId} />
}

export type VersionConfirmHandler = (arg: {
  id: string
  version?: GenerationModelVersionPreviewFragment
  model?: GenerationModelDetailFragment
}) => void
